import { defineStore } from "pinia";
import { type ActiveItem, type FilterPill, ItemSelection } from "~/components/documents/utils";
import { useItemStore } from "./item";
import { piniaPluginPersistedstate } from "#imports";
import type { Item, ItemPaginationResponseObject } from "~~/api/models";
import { useWidgetStore } from "./widget";
import { useEventStore } from '~/store/useEvents';

export const useItemSelectionStore = defineStore("itemSelection", () => {

  /*
   * State
   */
  const storeEvent = useEventStore();
  const navigationalItems = ref<ItemPaginationResponseObject>({
    totalCount: 0,
    data: [],
    page: 1,
    pageSize: storeEvent.defaultPagination,
    totalPages: 0,
    hasPreviousPage: false,
    hasNextPage: false
  } as ItemPaginationResponseObject);
  // What type of selection are we making, by default, we're only looking at a selection of documents
  const selectionMode = ref<ItemSelection>(ItemSelection.NONE);
  // List of selected items.
  const selectedItems = ref<string[]>([]);
  const activeItems = ref<ActiveItem[]>([]);
  const nextActiveItem = ref<ActiveItem | null>(null);
  const previousActiveItem = ref<ActiveItem | null>(null);
  const nextItem = ref<Item | null>(null);
  const nextItemPage = ref<number>(1);
  const previousItem = ref<Item | null>(null);
  const previousItemPage = ref<number>(1);
  const activeFilters = ref<FilterPill[]>([]);
  const activeSorting = ref<string>("DueDate:Desc");
  // Fields the navigation needs
  const itemFields = "id,tenantId,invoiceNo,creditor";

  // Composable
  const itemStore = useItemStore();
  const widgetStore = useWidgetStore();

  /*
   * Getters
   */
  const amountOfActiveDocuments = computed<number>(() => {
    // The count of active documents is based on the selection mode. If we have a selection, the count will be the selectedItems.
    // If we don't have a selection, the count will be the number of items within the itemStore.items.totalCount
    switch (selectionMode.value) {
      case ItemSelection.ALL:
        return navigationalItems.value.totalCount ?? 0;
      case ItemSelection.SELECTION:
        return activeItems.value.length;
      case ItemSelection.NONE:
        return activeItems.value.length;
      default:
        return activeItems.value.length;
    }
  });
  const currentSelectedItemIndex = computed<number>(() => {
    if (!itemStore.workingItem) return -1;
    if (activeItems.value.length === 0) return -1;

    return activeItems.value.findIndex(i => i.itemId === itemStore.workingItem?.id) ?? -1;
  });

  /*
   * Methods
   */
  const getNextActiveItem = async (): Promise<ActiveItem | null> => {
    if (currentSelectedItemIndex.value === -1) return null;
    const nextIdx = currentSelectedItemIndex.value + 1;

    switch (selectionMode.value) {
      case ItemSelection.SELECTION:
        // If the next index exists within the activeItems
        if (nextIdx < activeItems.value.length) {
          const next = activeItems.value[currentSelectedItemIndex.value + 1];

          if (next) {
            nextItem.value = itemStore.items.data?.find(i => i.id === next.itemId) ?? null;
            if (!nextItem.value) {
              nextItem.value = await itemStore.getItemById(next.itemId, itemFields, widgetStore.activeWidget?.normalizedName) ?? null;
            }

            return next;
          }
        }

        return null;
      case ItemSelection.ALL: {
        // still within the current page?
        if (nextIdx < activeItems.value.length) {
          const next = activeItems.value[nextIdx];
          // If we're within the current page, that means that the navigationalItems include the next index
          nextItem.value = navigationalItems.value.data?.find(i => i.id === next.itemId) ?? null;
          nextItemPage.value = navigationalItems.value.page;
          return next;
        }
        // otherwise, if there’s another page of items, fetch it and return the first item in that page.
        if (navigationalItems.value.hasNextPage) {
          const nextPage = await itemStore.getItems(
            navigationalItems.value.page + 1,
            navigationalItems.value.pageSize,
            itemFields,
            activeFilters.value,
            activeSorting.value
          );

          if (nextPage.data && nextPage.data.length > 0) {
            nextItem.value = nextPage.data[0];
            nextItemPage.value = nextPage.page;

            return { itemId: nextItem.value.id, tenantId: nextItem.value.tenantId } as ActiveItem;
          }
        }
        return null;
      }

      default:
        return null;
    }
  };
  const getPreviousActiveItem = async (): Promise<ActiveItem | null> => {
    // const currIdx = selectedItems.value.findIndex(i => i.itemId === workingId);
    if (currentSelectedItemIndex.value === -1) return null;
    const prevIdx = currentSelectedItemIndex.value - 1;

    switch (selectionMode.value) {
      case ItemSelection.SELECTION:
        // If the previous index exists within the activeItems, return that
        if (prevIdx >= 0) {
          const prev = activeItems.value[prevIdx];
          if (prev) {
            previousItem.value = itemStore.items.data?.find(i => i.id === prev.itemId) ?? null;
            if (!previousItem.value) {
              previousItem.value = await itemStore.getItemById(prev.itemId, itemFields, widgetStore.activeWidget?.normalizedName) ?? null;
            }
            return prev;
          }
        }

        return null;
      case ItemSelection.ALL: {
        // still within the current page?
        if (prevIdx >= 0) {
          const previous = activeItems.value[prevIdx];
          // If we're within the current page, that means that the navigationalItems will include the previous index.
          previousItem.value = navigationalItems.value.data?.find(i => i.id === previous.itemId) ?? null;
          previousItemPage.value = navigationalItems.value.page;
          return previous;
        }
        // otherwise, if there’s a previous page
        if (navigationalItems.value.hasPreviousPage) {
          const prevPage = await itemStore.getItems(
            navigationalItems.value.page - 1,
            navigationalItems.value.pageSize,
            itemFields,
            activeFilters.value,
            activeSorting.value
          );

          if (prevPage.data) {
            previousItem.value = prevPage.data[prevPage.data.length - 1];
            previousItemPage.value = prevPage.page;
            return { itemId: previousItem.value.id, tenantId: previousItem.value.tenantId } as ActiveItem;
          }
        }
        return null;
      }

      default:
        return null;
    }
  };

  const getSelection = async (page: number = 1, pageSize: number = storeEvent.defaultPagination, mode: ItemSelection = ItemSelection.NONE, sort: string = "DueDate:Desc") => {
    // Set the cached mode
    selectionMode.value = mode;

    // Upon loading a page, we would need to determine the list of selected items. This list can contain some user-selected items, or simply the list of documents in the active widget.
    switch (selectionMode.value) {
      case ItemSelection.NONE:
        clearSelectedItems();
        if (itemStore.workingItem) {
          setSelection([itemStore.workingItem]);
        }
        nextActiveItem.value = null;
        previousActiveItem.value = null;
        return activeItems.value;
      case ItemSelection.SELECTION:
        if (selectedItems.value.length > 0) {
          setActiveItemsFromSelected();
        }
        // Upon getting the selections, but it is empty, we should check to see if the itemStore has a working item. And insert that into the selectedItems array
        if (activeItems.value.length === 0 && itemStore.workingItem) {
          setSelection([itemStore.workingItem]);
        }

        if (itemStore.workingItem) {
          nextActiveItem.value = await getNextActiveItem();
          previousActiveItem.value = await getPreviousActiveItem();
        }
        return activeItems.value;
      case ItemSelection.ALL: {
        clearSelectedItems();
        // When the selection is 'ALL' we should use the apiService to fetch items in the widget
        const normalizedWidgetName: string | null = widgetStore.activeWidget?.normalizedName ?? null;
        const cachedFilters = itemStore.getCachedFilters(normalizedWidgetName as string);
        activeFilters.value = cachedFilters;
        activeSorting.value = sort;

        navigationalItems.value = await itemStore.getItems(page, pageSize, itemFields, cachedFilters, activeSorting.value);

        // Upon having fetched fresh navigationalItems, we should set those as active:
        if (navigationalItems.value.data)
          setSelection(navigationalItems.value.data);

        if (itemStore.workingItem) {
          nextActiveItem.value = await getNextActiveItem();
          previousActiveItem.value = await getPreviousActiveItem();
        }

        return activeItems.value;
      }
    }
  };

  function setSelection(items: ActiveItem[] | Item[]): void {
    if (items.length === 0) throw new Error("Cannot set an empty selection");

    if (items.length > 0) {
      if ("id" in items[0]) {
        activeItems.value = (items as Item[]).map(i => ({
          itemId: i.id,
          tenantId: i.tenantId
        })) as ActiveItem[];
      } else {
        activeItems.value = items as ActiveItem[];
      }
    }
  }

  const setActiveItemsFromSelected = () => {
    if (selectedItems.value.length === 0) return;

    const items = selectedItems.value.map(i => {
      const [itemId, tenantId] = i.split(":");
      return { itemId, tenantId };
    }) as ActiveItem[];

    setSelection(items);
  };

  const clearSelectedItems = () => {
    selectedItems.value = [];
  };

  /*
    Navigation
   */
  const navigateToNextItem = async () => {
    if (nextActiveItem.value === null) return;

    await navigateTo({
      name: "documents-widget-tenant-item",
      params: {
        widget: widgetStore.activeWidget?.normalizedName,
        tenant: nextActiveItem.value.tenantId,
        item: nextActiveItem.value.itemId
      },
      query: {
        page: nextItemPage.value,
        pageSize: navigationalItems.value.pageSize,
        mode: selectionMode.value,
        sort: activeSorting.value
      }
    });
  };
  const navigateToPreviousItem = async () => {
    if (previousActiveItem.value === null) return;

    await navigateTo({
      name: "documents-widget-tenant-item",
      params: {
        widget: widgetStore.activeWidget?.normalizedName,
        tenant: previousActiveItem.value.tenantId,
        item: previousActiveItem.value.itemId
      },
      query: {
        page: previousItemPage.value,
        pageSize: navigationalItems.value.pageSize,
        mode: selectionMode.value,
        sort: activeSorting.value
      }
    });
  };

  /*
   * Watchers
   */
  // Watch when the item changes for setting the active items
  watch(
    () => itemStore.workingItem,
    async (newItem) => {
      if (newItem) {
        nextActiveItem.value = await getNextActiveItem();
        previousActiveItem.value = await getPreviousActiveItem();
      }
    }
  );

  return {
    navigationalItems,
    selectionMode,
    selectedItems,
    activeItems,
    amountOfActiveDocuments,
    nextActiveItem,
    previousActiveItem,
    nextItem,
    previousItem,

    getNextActiveItem,
    getPreviousActiveItem,
    getSelection,
    setSelection,
    setActiveItemsFromSelected,
    clearSelectedItems,
    navigateToNextItem,
    navigateToPreviousItem
  };
}, {
  persist: [
    {
      pick: ["selectionMode"],
      storage: piniaPluginPersistedstate.cookies({ sameSite: "strict" }),
      key: "itemSelection.navigation"
    },
    {
      pick: ["activeItems", "selectedItems"],
      storage: piniaPluginPersistedstate.cookies({ sameSite: "strict" }),
      key: "itemSelection.selectedItems"
    }
  ]
});